import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Info, X } from 'lucide-react';
import type { BrandProfileView } from '@mg/shared';
import { api } from '../../../lib/api';
import { Button } from '../../../components/ui/Button';
import { adoptBrandProfile } from '../../brand/queries';

/** The one-time notice after the legacy migration (spec:468). */
export const MIGRATION_NOTICE_TEXT = "We've sorted your previous guidance into the new fields. Review and save.";
const DISMISS_FAILED_TEXT = "Couldn't close this notice. Try again.";

/**
 * Shown to admins on a migrated workspace until it is dismissed, or until the first save of brand
 * content (the writer closes it then: final-design §2.7 item 10). Per workspace: one admin's dismissal
 * closes it for all (owner question 16). Closing it saves nothing of the customer's settings, so there
 * is no toast.
 */
export function MigrationNotice({
  tenantId,
  onDismissed,
}: {
  tenantId: string;
  /** The view the dismissal answered with. */
  onDismissed: (next: BrandProfileView) => void;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dismiss = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.dismissMigrationNotice(tenantId);
      adoptBrandProfile(queryClient, next);
      onDismissed(next);
    } catch {
      setError(DISMISS_FAILED_TEXT);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-line bg-page px-4 py-3">
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
        <p className="min-w-0 flex-1 text-sm text-ink">{MIGRATION_NOTICE_TEXT}</p>
        <Button
          type="button"
          variant="plain"
          size="sm"
          className="-my-1 shrink-0"
          aria-label="Dismiss notice"
          loading={busy}
          onClick={() => void dismiss()}
        >
          {!busy && <X className="h-4 w-4" aria-hidden />}
        </Button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}
    </div>
  );
}
